/**
 * Centralized Notification Store
 *
 * Global Zustand store for managing notification toasts across all pages.
 * This enables:
 * - Global NotificationBell in GlobalHeader (shared inbox across pages)
 * - Global NotificationToastStack in AuthenticatedLayout
 * - Deduplication of notifications (prevents duplicates from real-time + FCM)
 * - Location-based filtering (clear toasts when switching locations)
 * - Offline queue for notifications created while offline
 * - Notification batching/grouping for rapid-fire notifications
 * - TTL-based auto-expiration of stale toasts
 *
 * Previously, notifications were managed per-page in unifiedUIStore.
 * This centralized store enables truly global notification management.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getNotificationConfig, notificationLogger } from '../config/notificationTypes';

/** Default TTL for toasts: 10 minutes */
const DEFAULT_TOAST_TTL_MS = 10 * 60 * 1000;

/** Window for grouping similar notifications: 30 seconds */
const GROUPING_WINDOW_MS = 30 * 1000;

/** LocalStorage key for tracking dismissed toast IDs */
const DISMISSED_TOASTS_KEY = 'botrtools_dismissed_toasts';

/** Max age for dismissed toast tracking: 24 hours (cleanup old entries) */
const DISMISSED_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * Load dismissed toast IDs from localStorage
 * Returns a map of id -> dismissedAt timestamp
 */
const loadDismissedToasts = (): Map<string, number> => {
  try {
    const stored = localStorage.getItem(DISMISSED_TOASTS_KEY);
    if (!stored) return new Map();
    const parsed = JSON.parse(stored) as Array<[string, number]>;
    return new Map(parsed);
  } catch {
    return new Map();
  }
};

/**
 * Save dismissed toast IDs to localStorage
 * Also cleans up entries older than 24 hours
 */
const saveDismissedToasts = (dismissed: Map<string, number>): void => {
  try {
    const now = Date.now();
    // Filter out old entries
    const cleaned = new Map<string, number>();
    for (const [id, timestamp] of dismissed) {
      if (now - timestamp < DISMISSED_MAX_AGE_MS) {
        cleaned.set(id, timestamp);
      }
    }
    localStorage.setItem(DISMISSED_TOASTS_KEY, JSON.stringify(Array.from(cleaned.entries())));
  } catch {
    // Ignore localStorage errors
  }
};

/**
 * Notification toast item structure
 * Matches the format used by useNotifications hook and FCM payloads
 */
export interface NotificationToastItem {
  id: string;
  type: string;
  title: string;
  body: string;
  locationId: string; // Location where notification originated
  metadata?: {
    itemId?: string;
    equipmentId?: string;
    [key: string]: unknown;
  };
  timestamp: number;
  actionBy?: string; // Email of user who triggered the notification
  actionByName?: string; // Display name of user who triggered the notification
  /** Internal: count of grouped notifications (for batching) */
  _groupCount?: number;
}

/**
 * Queued notification for offline support
 * Stores the payload to send once back online
 */
interface QueuedNotification {
  payload: {
    type: string;
    title: string;
    body: string;
    targetRole: string;
    locationId: string;
    metadata: Record<string, unknown>;
  };
  timestamp: number;
  retryCount: number;
}

interface NotificationState {
  /** Active notification toasts (stacking, persistent until dismissed) */
  toasts: NotificationToastItem[];

  /** Queue of notifications to send when back online */
  offlineQueue: QueuedNotification[];

  /**
   * Add a toast to the stack
   * Automatically deduplicates by ID - if a toast with the same ID exists, it won't be added
   * Supports grouping: if groupKey matches and within time window, increments count instead
   * @param toast - Toast item (timestamp is optional, defaults to Date.now())
   */
  addToast: (toast: Omit<NotificationToastItem, 'timestamp'> & { timestamp?: number }) => void;

  /**
   * Remove a specific toast by ID
   * @param id - The toast ID to remove
   */
  removeToast: (id: string) => void;

  /**
   * Clear all toasts
   */
  clearToasts: () => void;

  /**
   * Clear only toasts for a specific location
   * Useful when switching locations to avoid showing stale notifications
   * @param locationId - The location ID to filter by
   */
  clearToastsByLocation: (locationId: string) => void;

  /**
   * Remove toasts that have exceeded their TTL
   * Called periodically from AuthenticatedLayout
   */
  cleanupStaleToasts: () => void;

  /**
   * Add a notification to the offline queue
   * @param payload - The notification payload to queue
   */
  addToOfflineQueue: (payload: QueuedNotification['payload']) => void;

  /**
   * Remove an item from the offline queue
   * @param timestamp - The timestamp of the item to remove
   */
  removeFromOfflineQueue: (timestamp: number) => void;

  /**
   * Get the current offline queue for processing
   */
  getOfflineQueue: () => QueuedNotification[];

  /**
   * Clear the entire offline queue
   */
  clearOfflineQueue: () => void;

  /**
   * Check if a toast has been dismissed by the user
   * @param id - The notification/toast ID to check
   */
  isToastDismissed: (id: string) => boolean;

  /**
   * Mark a toast as dismissed (persisted to localStorage)
   * @param id - The notification/toast ID to mark as dismissed
   */
  markToastDismissed: (id: string) => void;

  /**
   * Clear all dismissed toast tracking
   * (useful for debugging or resetting state)
   */
  clearDismissedToasts: () => void;
}

export const useNotificationStore = create<NotificationState>()(
  devtools(
    (set, get) => ({
      toasts: [],
      offlineQueue: [],

      addToast: (toast) =>
        set((state) => {
          // Deduplication: Check if toast with same ID already exists
          if (state.toasts.some((t) => t.id === toast.id)) {
            notificationLogger.log('Duplicate toast ignored:', toast.id);
            return state; // Don't add duplicate
          }

          const config = getNotificationConfig(toast.type);
          const now = Date.now();

          // Grouping: If this type has a groupKey, check for recent similar toasts
          if (config.groupKey) {
            const existingGroupToast = state.toasts.find((t) => {
              const tConfig = getNotificationConfig(t.type);
              return (
                tConfig.groupKey === config.groupKey &&
                t.locationId === toast.locationId &&
                now - t.timestamp < GROUPING_WINDOW_MS
              );
            });

            if (existingGroupToast) {
              // Update existing toast with incremented count
              const newCount = (existingGroupToast._groupCount || 1) + 1;
              notificationLogger.log('Grouping toast:', toast.id, 'with', existingGroupToast.id, 'count:', newCount);

              return {
                toasts: state.toasts.map((t) =>
                  t.id === existingGroupToast.id
                    ? {
                        ...t,
                        _groupCount: newCount,
                        body: `${newCount} items`,
                        timestamp: now, // Refresh timestamp so it stays visible
                      }
                    : t
                ),
              };
            }
          }

          notificationLogger.event('toast_added', { id: toast.id, type: toast.type });

          return {
            toasts: [
              ...state.toasts,
              {
                ...toast,
                timestamp: toast.timestamp ?? now,
              },
            ],
          };
        }),

      removeToast: (id) => {
        // Also mark as dismissed so it won't reappear on page reload
        const dismissed = loadDismissedToasts();
        dismissed.set(id, Date.now());
        saveDismissedToasts(dismissed);

        set((state) => {
          notificationLogger.event('toast_removed', { id });
          return {
            toasts: state.toasts.filter((t) => t.id !== id),
          };
        });
      },

      clearToasts: () => {
        // Mark all current toasts as dismissed before clearing
        const state = get();
        const dismissed = loadDismissedToasts();
        for (const toast of state.toasts) {
          dismissed.set(toast.id, Date.now());
        }
        saveDismissedToasts(dismissed);

        notificationLogger.log('Clearing all toasts');
        return set({ toasts: [] });
      },

      clearToastsByLocation: (locationId) => {
        // Mark toasts for this location as dismissed before clearing
        const state = get();
        const dismissed = loadDismissedToasts();
        for (const toast of state.toasts) {
          if (toast.locationId === locationId) {
            dismissed.set(toast.id, Date.now());
          }
        }
        saveDismissedToasts(dismissed);

        return set((state) => {
          notificationLogger.log('Clearing toasts for location:', locationId);
          return {
            toasts: state.toasts.filter((t) => t.locationId !== locationId),
          };
        });
      },

      cleanupStaleToasts: () =>
        set((state) => {
          const now = Date.now();
          const staleIds: string[] = [];

          const freshToasts = state.toasts.filter((toast) => {
            const config = getNotificationConfig(toast.type);
            // Use type-specific TTL if defined, otherwise default
            const ttl = config.autoDismissMs > 0 ? config.autoDismissMs : DEFAULT_TOAST_TTL_MS;
            const isStale = now - toast.timestamp > ttl;

            if (isStale) {
              staleIds.push(toast.id);
            }
            return !isStale;
          });

          if (staleIds.length > 0) {
            notificationLogger.log('Cleaned up stale toasts:', staleIds);
          }

          return { toasts: freshToasts };
        }),

      addToOfflineQueue: (payload) =>
        set((state) => {
          notificationLogger.log('Adding to offline queue:', payload.type);
          return {
            offlineQueue: [
              ...state.offlineQueue,
              {
                payload,
                timestamp: Date.now(),
                retryCount: 0,
              },
            ],
          };
        }),

      removeFromOfflineQueue: (timestamp) =>
        set((state) => ({
          offlineQueue: state.offlineQueue.filter((q) => q.timestamp !== timestamp),
        })),

      getOfflineQueue: () => get().offlineQueue,

      clearOfflineQueue: () => {
        notificationLogger.log('Clearing offline queue');
        return set({ offlineQueue: [] });
      },

      isToastDismissed: (id) => {
        const dismissed = loadDismissedToasts();
        return dismissed.has(id);
      },

      markToastDismissed: (id) => {
        const dismissed = loadDismissedToasts();
        dismissed.set(id, Date.now());
        saveDismissedToasts(dismissed);
        notificationLogger.log('Marked toast as dismissed:', id);
      },

      clearDismissedToasts: () => {
        try {
          localStorage.removeItem(DISMISSED_TOASTS_KEY);
          notificationLogger.log('Cleared all dismissed toast tracking');
        } catch {
          // Ignore localStorage errors
        }
      },
    }),
    { name: 'NotificationStore' }
  )
);
